/**
 * 포트·reader·writer 를 소유하는 유일한 곳.
 *
 * 여기서 만든 writer 외에 다른 writer 를 만들지 않는다. 정리 순서도 여기서
 * 고정한다 — reader.cancel() → reader lock 해제 → writer lock 해제 →
 * port.close(). writer.close() 로 포트 종료를 대신하지 않는다.
 */

/** Web Serial 의 SerialPort 중 우리가 쓰는 부분만. 가상 포트도 이 모양이면 된다. */
export interface PortLike {
  open(options: SerialOpenOptions): Promise<void>;
  close(): Promise<void>;
  readonly readable: ReadableStream<Uint8Array> | null;
  readonly writable: WritableStream<Uint8Array> | null;
  getInfo(): { usbVendorId?: number; usbProductId?: number };
}

export interface SerialOpenOptions {
  baudRate: number;
  dataBits: 8;
  stopBits: 1;
  parity: "none";
  flowControl: "none";
}

export interface SerialLike {
  requestPort(options?: { filters?: Array<{ usbVendorId: number; usbProductId?: number }> }): Promise<PortLike>;
  getPorts(): Promise<PortLike[]>;
  addEventListener(type: string, listener: (event: Event) => void): void;
  removeEventListener(type: string, listener: (event: Event) => void): void;
}

/** 네오피아 동글 — CP210x (VID 0x10C4). */
export const NEOPIA_DONGLE_FILTER = { usbVendorId: 0x10c4 };

export function getSerial(): SerialLike | null {
  const serial = (navigator as unknown as { serial?: SerialLike }).serial;
  return serial ?? null;
}

export function isSupported(): boolean {
  return typeof isSecureContext !== "undefined" && isSecureContext && getSerial() !== null;
}

export function describePort(port: PortLike): { usbVendorId: number | null; usbProductId: number | null } {
  const info = port.getInfo();
  return {
    usbVendorId: info.usbVendorId ?? null,
    usbProductId: info.usbProductId ?? null,
  };
}

export function openOptions(baudRate: number): SerialOpenOptions {
  // 500000 이 열리지 않는다고 115200 으로 바꿔 열지 않는다 — 제품이 알아듣지 못한다.
  return { baudRate, dataBits: 8, stopBits: 1, parity: "none", flowControl: "none" };
}

export interface ReadChunk {
  value: Uint8Array | undefined;
  done: boolean;
}

export class SerialTransport {
  private port: PortLike | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private opened = false;
  private readCancelled = false;

  constructor(port: PortLike) {
    this.port = port;
  }

  get isOpen(): boolean {
    return this.opened;
  }

  info(): { usbVendorId: number | null; usbProductId: number | null } {
    return this.port ? describePort(this.port) : { usbVendorId: null, usbProductId: null };
  }

  async open(baudRate: number): Promise<void> {
    if (!this.port) throw new Error("포트가 없습니다");
    await this.port.open(openOptions(baudRate));
    this.opened = true;
    if (!this.port.readable || !this.port.writable) {
      throw new Error("포트를 열었지만 읽기·쓰기 스트림이 없습니다");
    }
    this.reader = this.port.readable.getReader();
    this.writer = this.port.writable.getWriter();
  }

  /** 이 전송 계층의 유일한 write. 완료를 기다리는 책임은 엔진에 있다. */
  write(data: Uint8Array): Promise<void> {
    if (!this.writer) return Promise.reject(new Error("writer 가 없습니다"));
    return this.writer.write(data);
  }

  read(): Promise<ReadChunk> {
    if (!this.reader) return Promise.reject(new Error("reader 가 없습니다"));
    return this.reader.read();
  }

  /** 이 전송 계층이 그 포트를 쥐고 있는가 — disconnect 이벤트 확인용. */
  owns(port: PortLike): boolean {
    return this.port === port;
  }

  /**
   * 수신을 취소한다. 대기 중인 read() 가 done 으로 풀려야 RX 루프가 끝나고,
   * 그래야 reader 잠금을 안전하게 놓을 수 있다.
   */
  async cancelRead(): Promise<void> {
    if (this.readCancelled || !this.reader) return;
    this.readCancelled = true;
    await this.reader.cancel();
  }

  /**
   * 정리. 실패해도 각 단계를 끝까지 시도하고 모인 오류를 돌려준다.
   * 여기서 성공을 가장하지 않는다 — 부르는 쪽이 오류 목록을 보고 판단한다.
   */
  async cleanup(): Promise<Error[]> {
    const errors: Error[] = [];
    const attempt = async (what: string, run: () => Promise<unknown>) => {
      try {
        await run();
      } catch (error) {
        errors.push(new Error(`${what}: ${String(error)}`));
      }
    };

    const reader = this.reader;
    if (reader) {
      if (!this.readCancelled) {
        this.readCancelled = true;
        await attempt("수신 취소", () => reader.cancel());
      }
      try {
        reader.releaseLock();
      } catch (error) {
        errors.push(new Error(`reader 잠금 해제: ${String(error)}`));
      }
      this.reader = null;
    }

    const writer = this.writer;
    if (writer) {
      try {
        writer.releaseLock();
      } catch (error) {
        errors.push(new Error(`writer 잠금 해제: ${String(error)}`));
      }
      this.writer = null;
    }

    const port = this.port;
    if (port && this.opened) {
      await attempt("포트 닫기", () => port.close());
      this.opened = false;
    }
    this.port = null;
    return errors;
  }
}
